import { useState, useLayoutEffect, useRef } from "react";
import useResizeObserver from "@react-hook/resize-observer";
import debounce from "lodash/debounce";

type DebounceSettings = {
  leading?: boolean;
  trailing?: boolean;
  maxWait?: number;
  freeze?: number;
}
export type UseSizeReturnType = { width: number | null, height: number | null } | null;

export function useSize<T extends HTMLElement>(
  target: T | null,
  debounceDelay: number = 250,
  disable?: boolean, 
  options?: DebounceSettings
) {
  if (disable) return null;
  const [size, setSize] = useState<DOMRect>();
  const sizeHistory = useRef<UseSizeReturnType>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRef = useRef(true);

  const debounced = debounce(({ contentRect }) => {
    setSize(contentRect);
  }, debounceDelay, options);

  const targetSize = disable ? null : target?.getBoundingClientRect();

  // Initialize
  useLayoutEffect(() => {
    if (target) {
      setSize(target.getBoundingClientRect());
    }
    return () => {
      debounced.cancel();
    };
  }, [target]);
  
  useLayoutEffect(()=>{
    if (!timerRef.current && size && options?.freeze) {
      activeRef.current = false;
      setTimeout(()=>{
        timerRef.current = setTimeout(()=>{
          activeRef.current = true;
          timerRef.current && clearTimeout(timerRef.current);
          timerRef.current = null;
        }, options?.freeze ?? 0);
      }, 50);
    } 
  }, [size]);

  // Setup RO
  const ro = useResizeObserver(target, debounced);
  if (target && disable) {
    ro.unobserve(target);
    debounced.cancel();
  }

  const sizes =  { width: size?.width || null, height: size?.height || null };
  if (disable || !activeRef.current) {
    debounced.cancel();
    return targetSize;
    // return sizeHistory.current ? sizeHistory.current : null
  };

  sizeHistory.current = sizes;
  return sizes;
}
